/**
 * Team Inbox — start a WhatsApp conversation with someone who has never
 * written to us (owner request, 2026-08-02).
 *
 * DELIBERATELY SMALL. This asks ONE question — who are you writing to? — and
 * then opens that conversation. It does NOT choose a template and it does NOT
 * send: the thread's own Send Picker already does both, with search, categories,
 * a real preview and the required-blanks gate. Duplicating that here would mean
 * two template UIs drifting apart (owner catch, 2026-08-02: "why can't we have
 * multiple templates and select based on the requirement" — we can, and the
 * picker that does it already exists).
 *
 * Because a first contact is always outside the 24-hour window, the thread
 * composer opens straight onto templates-only. So the flow is:
 *   pick who → land in the conversation → pick any approved template → send.
 */
import { useMemo, useState } from 'react';
import type { LiveClient } from '../../../lib/projectStore';
import { filterContactsForPicker } from '../lib/attachExisting';

/** Digits-only, for the "is this a number?" test and for the callable. */
function digitsOf(s: string): string {
  return (s || '').replace(/[^\d]/g, '');
}

/** The best phone we hold for a contact — top-level first, then the list. */
export function contactPhone(c: LiveClient): string {
  if (c.phone) return digitsOf(c.phone);
  for (const e of c.phones ?? []) {
    if (e?.number) return digitsOf(e.number);
  }
  return '';
}

/**
 * PURE — should we offer "message this new number"? Only when the typed text
 * is a plausible full number WITH a country code, so we never invent one.
 */
export function offerNewNumber(query: string): string | null {
  const d = digitsOf(query);
  if (d.length < 11 || d.length > 15) return null;
  return d;
}

export default function ComposeWhatsApp({
  senderName,
  contacts,
  busy,
  err,
  onClose,
  onPick,
}: {
  /** Shown so it is obvious which name the client will see. */
  senderName: string;
  /** The already-loaded contacts stream — no extra Firestore read. */
  contacts: LiveClient[];
  busy: boolean;
  err: string | null;
  onClose: () => void;
  /** Open (or find) the conversation for this number, then hand over to it. */
  onPick: (phone: string, name: string) => Promise<boolean>;
}) {
  const [q, setQ] = useState('');

  const results = useMemo(
    () => filterContactsForPicker(contacts, q, 25).filter((c) => contactPhone(c)),
    [contacts, q],
  );
  const newNumber = offerNewNumber(q);
  // Only offer "new number" when no saved contact already holds it — otherwise
  // we would invite a duplicate thread against someone we already know.
  const showNewNumber =
    !!newNumber && !results.some((c) => contactPhone(c) === newNumber);

  async function choose(phone: string, name: string) {
    if (busy || !phone) return;
    const ok = await onPick(phone, name);
    if (ok) onClose();
  }

  return (
    <div
      className="ti-modal-backdrop"
      role="dialog"
      aria-modal
      aria-label="New WhatsApp message"
    >
      <div className="ti-compose">
        <div className="ti-compose-head">
          <h3>New WhatsApp message</h3>
          <button
            type="button"
            className="ti-compose-x"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="ti-compose-from">
          Sending as <b>{senderName}</b> from +91 80154 17121
        </div>

        <label className="ti-compose-field">
          <span>Who to</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a contact, or type a number with 91…"
            autoFocus
          />
        </label>

        <div className="ti-wa-results">
          {showNewNumber && newNumber && (
            <button
              type="button"
              className="ti-wa-result ti-wa-result-new"
              disabled={busy}
              onClick={() => void choose(newNumber, '')}
            >
              <span className="ti-wa-result-name">Message {newNumber}</span>
              <span className="ti-wa-result-sub">
                Not a saved contact — starts a new conversation
              </span>
            </button>
          )}

          {results.map((c) => {
            const phone = contactPhone(c);
            return (
              <button
                type="button"
                key={c.cid}
                className="ti-wa-result"
                disabled={busy}
                onClick={() => void choose(phone, c.name || '')}
              >
                <span className="ti-wa-result-name">{c.name || phone}</span>
                <span className="ti-wa-result-sub">
                  {c.clientNo ? `${c.clientNo} · ` : ''}
                  {phone}
                </span>
              </button>
            );
          })}

          {!results.length && !showNewNumber && (
            <p className="ti-compose-hint">
              {q.trim()
                ? 'No contact matches. To message a new number, type it in full with the country code — 91 for India.'
                : 'Start typing a name or a number.'}
            </p>
          )}
        </div>

        {err && (
          <div className="ti-send-err" role="alert">
            {err}
          </div>
        )}

        <div className="ti-compose-row">
          <span className="ti-compose-hint">
            {busy
              ? 'Opening the conversation…'
              : 'They have not written to us, so the next step is choosing an approved template.'}
          </span>
        </div>
      </div>
    </div>
  );
}
